import { useEffect, useRef, useState } from 'react'

import { ChevronDownIcon } from '@ui/components/icons'
import clsx from 'clsx'

export interface DropdownOption<T> {
  value: T
  label: string
}

interface DropdownProps<T> {
  options: DropdownOption<T>[]
  selectedOption: T | null
  onOptionSelect: (value: T) => void
  placeholder?: string
  extraElement?: React.ReactNode
  containerClassName?: string
  buttonClassName?: string
  listClassName?: string
}

/**
 * Creates a dropdown menu
 * @param options - Array of DropdownOption objects
 * @param selectedOption - The currently selected option
 * @param onOptionSelect - Callback function to handle option selection
 * @param placeholder - Placeholder text for the dropdown button
 * @param extraElement - Additional element to render inside the dropdown button
 * @param containerClassName - Additional classes to apply to the dropdown container
 * @param buttonClassName - Additional classes to apply to the dropdown button
 * @param listClassName - Additional classes to apply to the dropdown list
 * @returns ReactElement
 */

export const Dropdown = <T,>({
  options,
  selectedOption,
  onOptionSelect,
  placeholder = 'Select an option',
  extraElement,
  containerClassName = '',
  buttonClassName = '',
  listClassName = '',
}: DropdownProps<T>) => {
  const [isOpen, setIsOpen] = useState(false)
  const dropdownRef = useRef<HTMLDivElement>(null)

  const toggleDropdown = () => setIsOpen((prev) => !prev)
  const handleOptionClick = (value: T) => {
    onOptionSelect(value)
    setIsOpen(false)
  }

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  return (
    <div ref={dropdownRef} className={clsx('relative', containerClassName)}>
      <div className="flex flex-row items-center justify-between space-x-0.5">
        <button
          className={clsx('w-full rounded border bg-white text-left focus:outline-none', buttonClassName)}
          onClick={toggleDropdown}>
          <p className="text-sm">
            {selectedOption ? options.find((option) => option.value === selectedOption)?.label : placeholder}
          </p>
        </button>

        {extraElement && extraElement}

        <ChevronDownIcon
          onClick={toggleDropdown}
          className={clsx('h-6 w-6 cursor-pointer stroke-gray-900 transition-transform', isOpen ? 'rotate-180' : '')}
        />
      </div>
      {isOpen && options.length > 0 && (
        <ul
          className={clsx('absolute z-10 mt-1 w-full rounded border bg-white shadow-md', listClassName)}
          role="listbox">
          {options.map((option: DropdownOption<T>, index: number) => (
            <li
              role="option"
              key={index}
              className="cursor-pointer px-4 py-2 hover:bg-gray-100"
              onClick={() => handleOptionClick(option.value)}>
              <p className="overflow-hidden text-ellipsis whitespace-nowrap text-sm">{option.label}</p>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}
